import type {InspectionScopeDeviceResponse} from 'src/api/ProactApiClient';
import {ComplianceStateEnum} from 'src/api/ProactApiClient';

// Reused by the offline runtime over the cached bundle — must stay dependency-free (no redux/api imports).

export const UNSPECIFIED_GROUP_LABEL = 'Unspecified';

export interface DeviceRollupCounts {
  /** Not yet recorded this inspection. */
  skipped: number;
  pass: number;
  failed: number;
  noAccess: number;
  total: number;
}

export interface DeviceGroupNode {
  key: string;
  label: string;
  counts: DeviceRollupCounts;
  /** Present on Building/Floor nodes. */
  children?: DeviceGroupNode[];
  /** Present on the leaf Room node. */
  devices?: InspectionScopeDeviceResponse[];
}

export const computeRollupCounts = (
  devices: InspectionScopeDeviceResponse[]
): DeviceRollupCounts => {
  const counts: DeviceRollupCounts = {
    skipped: 0,
    pass: 0,
    failed: 0,
    noAccess: 0,
    total: devices.length,
  };

  devices.forEach(device => {
    switch (device.complianceState) {
      case ComplianceStateEnum.Pass:
        counts.pass += 1;
        break;
      case ComplianceStateEnum.Fail:
        counts.failed += 1;
        break;
      case ComplianceStateEnum.NoAccess:
        counts.noAccess += 1;
        break;
      default:
        counts.skipped += 1;
        break;
    }
  });

  return counts;
};

/** Partially recorded — some devices in the group are entered, some are not. */
export const isPartialGroup = (counts: DeviceRollupCounts): boolean =>
  counts.skipped > 0 && counts.skipped < counts.total;

export const groupLabel = (value?: string): string =>
  value?.trim() || UNSPECIFIED_GROUP_LABEL;

const groupBy = <T>(
  items: T[],
  keyFn: (item: T) => string
): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  items.forEach(item => {
    const key = keyFn(item);
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  });
  return map;
};

const sortGroupEntries = <T>(map: Map<string, T[]>) =>
  [...map.entries()].sort(([a], [b]) => {
    if (a === UNSPECIFIED_GROUP_LABEL) return 1;
    if (b === UNSPECIFIED_GROUP_LABEL) return -1;
    return a.localeCompare(b);
  });

/** Builds the nested Building → Floor → Room tree with per-node roll-up counts. */
export const buildDeviceGroupTree = (
  devices: InspectionScopeDeviceResponse[]
): DeviceGroupNode[] => {
  const byBuilding = groupBy(devices, d => groupLabel(d.building));

  return sortGroupEntries(byBuilding).map(([building, buildingDevices]) => {
    const byFloor = groupBy(buildingDevices, d => groupLabel(d.floor));

    const floors: DeviceGroupNode[] = sortGroupEntries(byFloor).map(
      ([floor, floorDevices]) => {
        const byRoom = groupBy(floorDevices, d => groupLabel(d.room));

        const rooms: DeviceGroupNode[] = sortGroupEntries(byRoom).map(
          ([room, roomDevices]) => ({
            key: `${building}__${floor}__${room}`,
            label: room,
            counts: computeRollupCounts(roomDevices),
            devices: roomDevices,
          })
        );

        return {
          key: `${building}__${floor}`,
          label: floor,
          counts: computeRollupCounts(floorDevices),
          children: rooms,
        };
      }
    );

    return {
      key: building,
      label: building,
      counts: computeRollupCounts(buildingDevices),
      children: floors,
    };
  });
};

/** Flattens the Building→Floor→Room tree back into a leaf-ordered device list ("Save & Next" order). */
export const flattenDeviceGroupTree = (
  tree: DeviceGroupNode[]
): InspectionScopeDeviceResponse[] => {
  const result: InspectionScopeDeviceResponse[] = [];
  const visit = (node: DeviceGroupNode) => {
    if (node.devices) {
      result.push(...node.devices);
      return;
    }
    (node.children ?? []).forEach(visit);
  };
  tree.forEach(visit);
  return result;
};

export const ALL_LOCATIONS_NODE_ID = 'all';

/** Same key scheme as `buildDeviceGroupTree` — Building[__Floor[__Room]]. */
export const deviceMatchesLocationNode = (
  device: InspectionScopeDeviceResponse,
  nodeId: string
): boolean => {
  if (nodeId === ALL_LOCATIONS_NODE_ID) return true;
  const parts = nodeId.split('__');
  const deviceParts = [
    groupLabel(device.building),
    groupLabel(device.floor),
    groupLabel(device.room),
  ];
  return parts.every((part, index) => deviceParts[index] === part);
};

/** Depth-first lookup of a node by its `key` (Building/Floor/Room). */
export const findDeviceGroupNode = (
  nodes: DeviceGroupNode[],
  key: string
): DeviceGroupNode | undefined => {
  for (const node of nodes) {
    if (node.key === key) return node;
    const found = node.children && findDeviceGroupNode(node.children, key);
    if (found) return found;
  }
  return undefined;
};

/** Building · Floor · Room label, dashed when none of the three are set. */
export const formatDeviceLocation = (device: InspectionScopeDeviceResponse): string =>
  [device.building, device.floor, device.room].filter(Boolean).join(' · ') || '—';

export interface FilterScopeDevicesOptions {
  nodeId: string;
  search: string;
  showOnlyRemaining: boolean;
}

/**
 * Search/tree/remaining are display predicates only — devices excluded by none of the
 * three (deleted, re-typed/un-tagged recorded rows) always render (invariant D3).
 */
export const filterScopeDevices = (
  devices: InspectionScopeDeviceResponse[],
  {nodeId, search, showOnlyRemaining}: FilterScopeDevicesOptions
): InspectionScopeDeviceResponse[] => {
  const term = search.trim().toLowerCase();
  return devices.filter(device => {
    if (!deviceMatchesLocationNode(device, nodeId)) return false;
    // D3 union rows: deleted devices stay visible under every display predicate.
    if (
      showOnlyRemaining &&
      device.complianceState != null &&
      !device.deviceIsDeleted
    )
      return false;
    if (term) {
      const haystack = `${device.name ?? ''} ${device.serialNumber ?? ''}`.toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    return true;
  });
};

/** First not-yet-entered device after `afterDeviceId` within `devices` (order preserved). */
export const findNextUnenteredDevice = (
  devices: InspectionScopeDeviceResponse[],
  afterDeviceId?: number
): InspectionScopeDeviceResponse | undefined => {
  const currentIndex = afterDeviceId
    ? devices.findIndex(d => d.deviceId === afterDeviceId)
    : -1;

  for (let i = currentIndex + 1; i < devices.length; i += 1) {
    if (devices[i].complianceState == null) return devices[i];
  }
  // Wrap: nothing left after the current device, look from the top —
  // excluding the current device itself (its snapshot may still be null).
  for (let i = 0; i < currentIndex; i += 1) {
    if (devices[i].complianceState == null) return devices[i];
  }
  return undefined;
};
